import { type JSXProps } from 'engine/index';
import { DropdownField, Form, PasswordField, SubmitButton, TextField, validator as V } from './FormComponents';
import { lateinit } from 'shared/util';
import { FormGroup } from './FormGroup';
import { displayRole, UserUpdateSchema, type User, type UserUpdate } from 'shared/modelTypes/index';
import { CheckCheck } from 'lucide-static';
import { showToast, Toast } from './Toast';
import { navigate } from 'engine/router';
import { rpc } from 'src/backend';

type UserEditProps = {
	user: User;
} & JSXProps;

export class UserEdit extends Base.Component<UserEditProps> {
	backLink: HTMLElement = lateinit;

	override render(): HTMLElement {
		const user = this.props.user;

		const matchPassword = (v?: string) => {
			const passwordField = document.getElementById('password-field') as HTMLInputElement;
			if (passwordField) {
				return v === passwordField.value ? passwordField.value : undefined;
			}
		};

		const view: HTMLElement = (
			<div class="flex flex-col gap-3">
				<h2 class="text-gray-700 dark:text-gray-300">Edit User</h2>
				<div class="w-fit rounded-lg bg-white p-6 shadow-md dark:bg-gray-800">
					<Form schema={UserUpdateSchema} onSubmit={this.create.bind(this)} class="flex flex-col gap-6">
						<input type="hidden" name="_id" value={user._id} />
						<div class="flex flex-col gap-6">
							<FormGroup>
								<DropdownField
									name="role"
									value={user.role}
									label="Role"
									validator={V(UserUpdateSchema.shape.role)}
									options={UserUpdateSchema.shape.role._def.values.map((k) => ({ value: k, label: displayRole(k) }))}
								/>
								<TextField
									name="firstName"
									value={user.firstName}
									label="First Name"
									validator={V(UserUpdateSchema.shape.firstName)}
									placeholder="John"
								/>
								<TextField
									name="lastName"
									value={user.lastName}
									label="Last Name"
									validator={V(UserUpdateSchema.shape.lastName)}
									placeholder="Doe"
								/>
								<TextField
									type="email"
									name="email"
									value={user.email}
									label="Email"
									validator={V(UserUpdateSchema.shape.email)}
									placeholder="johndoe@example.com"
								/>
							</FormGroup>
							<FormGroup title="Change password">
								<small class="text-gray-600 dark:text-gray-400">Leave empty to keep as-is.</small>
								<PasswordField
									strengthIndicator
									optional
									compact
									labelWidth="15ch"
									name="password"
									label="Password"
									validator={V(UserUpdateSchema.shape.password)}
								/>
								<PasswordField
									optional
									compact
									labelWidth="15ch"
									name="repeat-password"
									label="Repeat Password"
									validator={V(
										UserUpdateSchema.shape.password.refine(matchPassword, 'Repeat Password and Password should match'),
									)}
								/>
							</FormGroup>
						</div>
						<SubmitButton>Update</SubmitButton>
					</Form>
				</div>
			</div>
		);

		return view;
	}

	async create(user: UserUpdate) {
		this.view.style.pointerEvents = 'none';
		this.view.style.opacity = '0.6';
		const res = await rpc.updateUser.query(user);
		// console.log('got res', res);
		navigate('/dashboard/users');
		showToast(<Toast icon={CheckCheck} title="User saved!" />, 3000);
	}
}
